import {
    ChangeDetectionStrategy, Component, inject
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { AsyncPipe } from "@angular/common";
import { COMMON_APP_SERVICE } from "../../../application/constants/app-tokens";
import { LIST_STATE_SERVICE } from "../../constants/tokens";
import { IListStateService } from "../../interfaces/list-state-service";
import { ButtonDirective } from "../../../application/directives/button/button.directive";

/**
 * A standalone component that displays the current processing and empty state of a list.
 *
 * Features:
 * - Shows loading state with Material icon and message
 * - Integrates with common app state service
 * - Uses OnPush change detection for better performance
 * - Automatically updates when processing state changes
 * - Can add a custom empty message as content
 *
 * @example
 * ```html
 * <ng-template matNoDataRow>
 *    <mgd-list-state></mgd-list-state>
 * </ng-template>
 * ```
 * @example
 * ```html
 * <ng-template matNoDataRow>
 *    <mgd-list-state>
 *        Custom empty message
 *    </mgd-list-state>
 * </ng-template>
 * ```
 */
@Component({
    selector: "mgd-list-state",
    templateUrl: "./list-state.component.html",
    styleUrls: ["./list-state.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconModule,
        AsyncPipe,
        ButtonDirective
    ]
})
export class ListStateComponent {
    /** Service that manages the application state including processing status */
    readonly appState = inject(COMMON_APP_SERVICE);

    /** Service that manages the list state. Optional — components not on the full list pattern omit this provider. */
    readonly state = inject(LIST_STATE_SERVICE, { optional: true }) as IListStateService | null;
}
